import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  approveJoinRequest,
  createGroup,
  deleteGroup,
  getGroupMembers,
  getGroupNotifPrefs,
  getJoinRequests,
  getMyGroups,
  leaveGroup,
  demoteGroupMember,
  promoteGroupMember,
  rejectJoinRequest,
  removeGroupMember,
  updateGroup,
  updateGroupNotifPrefs,
  type GroupNotifPrefs,
} from '@/api/groups';
import type { CreateGroupPayload, Group, UpdateGroupPayload } from '@/types';

export const GROUPS_QUERY_KEY = ['my-groups'] as const;
const membersKey = (groupId: number) => ['group-members', groupId] as const;
const requestsKey = (groupId: number) => ['group-requests', groupId] as const;

/** The user's circles (Racore Official first) + create. */
export function useGroups() {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: GROUPS_QUERY_KEY,
    queryFn: () => getMyGroups(1, 50),
    staleTime: 1000 * 60 * 5,
  });

  const groups: Group[] = data?.data ?? [];

  const createGroupMutation = useMutation({
    mutationFn: (payload: CreateGroupPayload) => createGroup(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: GROUPS_QUERY_KEY }),
  });

  return {
    groups,
    isLoading,
    error,
    refetch,
    createGroup: createGroupMutation.mutateAsync,
    isCreating: createGroupMutation.isPending,
  };
}

/** Members of a circle. */
export function useGroupMembers(groupId: number | null) {
  return useQuery({
    queryKey: membersKey(groupId ?? -1),
    queryFn: () => getGroupMembers(groupId as number),
    enabled: groupId != null,
  });
}

/** Pending join requests for a circle (admin only). */
export function useJoinRequests(groupId: number | null, isAdmin: boolean) {
  return useQuery({
    queryKey: requestsKey(groupId ?? -1),
    queryFn: () => getJoinRequests(groupId as number),
    enabled: groupId != null && isAdmin,
  });
}

/** Mutations for managing a single circle (edit / delete / leave / remove member). */
export function useManageGroup(groupId: number) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: GROUPS_QUERY_KEY });
    qc.invalidateQueries({ queryKey: membersKey(groupId) });
  };

  const update = useMutation({
    mutationFn: (payload: UpdateGroupPayload) => updateGroup(groupId, payload),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => deleteGroup(groupId),
    onSuccess: () => qc.invalidateQueries({ queryKey: GROUPS_QUERY_KEY }),
  });
  const leave = useMutation({
    mutationFn: () => leaveGroup(groupId),
    onSuccess: () => qc.invalidateQueries({ queryKey: GROUPS_QUERY_KEY }),
  });
  const removeMember = useMutation({
    mutationFn: (userId: number) => removeGroupMember(groupId, userId),
    onSuccess: invalidate,
  });
  const promote = useMutation({
    mutationFn: (userId: number) => promoteGroupMember(groupId, userId),
    onSuccess: invalidate,
  });
  const demote = useMutation({
    mutationFn: (userId: number) => demoteGroupMember(groupId, userId),
    onSuccess: invalidate,
  });
  const approve = useMutation({
    mutationFn: (userId: number) => approveJoinRequest(groupId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: requestsKey(groupId) });
      qc.invalidateQueries({ queryKey: membersKey(groupId) });
    },
  });
  const reject = useMutation({
    mutationFn: (userId: number) => rejectJoinRequest(groupId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: requestsKey(groupId) }),
  });

  return { update, remove, leave, removeMember, promote, demote, approve, reject };
}

// ─── A6 — per-group notification preferences ────────────────────────────────

const groupNotifPrefsKey = (groupId: number) => ['group-notif-prefs', groupId] as const;

const DEFAULT_GROUP_NOTIF_PREFS: GroupNotifPrefs = {
  notif_new_sound: true,
  notif_member_events: true,
  notif_replies: true,
};

export function useGroupNotifPrefs(groupId: number, enabled = true) {
  return useQuery({
    queryKey: groupNotifPrefsKey(groupId),
    queryFn: () => getGroupNotifPrefs(groupId),
    staleTime: 5 * 60 * 1000,
    placeholderData: DEFAULT_GROUP_NOTIF_PREFS,
    enabled,
  });
}

/** Optimistic toggle — the switch flips instantly, rolls back on error. */
export function useUpdateGroupNotifPrefs(groupId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (update: Partial<GroupNotifPrefs>) => updateGroupNotifPrefs(groupId, update),
    onMutate: async (update) => {
      await qc.cancelQueries({ queryKey: groupNotifPrefsKey(groupId) });
      const previous = qc.getQueryData<GroupNotifPrefs>(groupNotifPrefsKey(groupId));
      qc.setQueryData<GroupNotifPrefs>(groupNotifPrefsKey(groupId), (old) => ({
        ...DEFAULT_GROUP_NOTIF_PREFS,
        ...old,
        ...update,
      }));
      return { previous };
    },
    onError: (_err, _update, context) => {
      if (context?.previous) qc.setQueryData(groupNotifPrefsKey(groupId), context.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: groupNotifPrefsKey(groupId) });
    },
  });
}
